import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import GavelIcon from '@mui/icons-material/Gavel';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useCompanyAiAgents } from '../../hooks/useCompanyAiAgents';
import AgentDialog from '../agents/AgentDialog';
import RemoveAgentDialog from '../agents/RemoveAgentDialog';
import {
  CODE_REVIEWER_AGENT_ID,
  CUSTOMER_SUCCESS_AGENT_ID,
  CSA_MODEL_TARGET,
  DEFAULT_MODEL_TARGET,
  buildCodeReviewerSeedPayload,
  buildCustomerSuccessSeedPayload,
  customerSuccessDocId,
  normalizeModelTarget,
  normalizeStudioAgent,
} from '../../lib/aiWorkforceRoster';
import { isAutoJudgeReviewEnabled } from '../../lib/autoJudgeReview';
import {
  clampMaxRejectionCount,
  DEFAULT_MAX_REJECTION_COUNT,
  MAX_MAX_REJECTION_COUNT,
  MIN_MAX_REJECTION_COUNT,
} from '../../lib/rejectionCap';

/** HQ workspace that owns cross-tenant support agents. */
const SHIFTYPM_HQ_COMPANY_ID = 'SHIFTYPM_HQ';

/**
 * Deterministic Firestore doc id for a company's Code Reviewer agent.
 * @param {string} companyId
 * @returns {string}
 */
function codeReviewerDocId(companyId) {
  const safe = String(companyId || '')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .slice(0, 80);
  return `code_reviewer_${safe || 'unknown'}`;
}

/**
 * @param {object} agent
 * @returns {boolean}
 */
function isCodeReviewerAgent(agent) {
  if (!agent) return false;
  if (agent.rosterRole === CODE_REVIEWER_AGENT_ID || agent.isCodeReviewer === true) {
    return true;
  }
  if (typeof agent.id === 'string' && agent.id.startsWith('code_reviewer')) {
    return true;
  }
  return false;
}

/**
 * @param {object} agent
 * @returns {boolean}
 */
function isCustomerSuccessAgent(agent) {
  if (!agent) return false;
  if (
    agent.rosterRole === CUSTOMER_SUCCESS_AGENT_ID ||
    agent.isCustomerSuccess === true
  ) {
    return true;
  }
  if (
    typeof agent.id === 'string' &&
    (agent.id === CUSTOMER_SUCCESS_AGENT_ID || agent.id.startsWith('csa_'))
  ) {
    return true;
  }
  return false;
}

/**
 * Map a roster row (+ raw `ai_agents` doc) into the shape AgentDialog expects.
 * Roster normalize uses `agentName`; AgentDialog reads `name` and full hire fields.
 * @param {object} rosterRow
 * @param {object[]} studioAgents
 * @returns {object}
 */
function toAgentDialogAgent(rosterRow, studioAgents) {
  const raw = (studioAgents || []).find((a) => a.id === rosterRow.id) || {};
  return {
    id: rosterRow.id,
    name: raw.name || rosterRow.agentName || '',
    roleTitle: raw.roleTitle || rosterRow.roleTitle || '',
    temperature: raw.temperature,
    systemPrompt: raw.systemPrompt || rosterRow.systemPrompt || '',
    default_boot_context:
      raw.default_boot_context || raw.defaultBootContext || '',
    allowedTools: raw.allowedTools ?? rosterRow.allowedTools,
    contextMemoryLimit: raw.contextMemoryLimit,
    modelTarget: raw.modelTarget || rosterRow.modelTarget,
    status: raw.status || rosterRow.status,
  };
}

/**
 * AI Workforce Roster — unified with AI Studio (`ai_agents` collection).
 * @param {{
 *   companyId?: string | null,
 *   frictionSuggestionId?: string | null,
 *   agentRoleHint?: string | null,
 *   onFrictionDeepLinkConsumed?: () => void,
 * }} props
 */
export default function AiWorkforceRosterPanel({
  companyId,
  frictionSuggestionId = null,
  agentRoleHint = null,
  onFrictionDeepLinkConsumed,
}) {
  const { agents: studioAgents, loading: agentsLoading, error: agentsError } =
    useCompanyAiAgents(companyId);

  const [hireOpen, setHireOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [removingAgent, setRemovingAgent] = useState(null);
  const [frictionDraftText, setFrictionDraftText] = useState(null);
  const [activeFrictionSuggestionId, setActiveFrictionSuggestionId] =
    useState(null);
  const [seedError, setSeedError] = useState('');
  const [seedingReviewer, setSeedingReviewer] = useState(false);
  const [seedingCsa, setSeedingCsa] = useState(false);
  const [autoJudgeReview, setAutoJudgeReview] = useState(true);
  const [autoJudgeLoading, setAutoJudgeLoading] = useState(Boolean(companyId));
  const [autoJudgeSaving, setAutoJudgeSaving] = useState(false);
  const [autoJudgeError, setAutoJudgeError] = useState('');
  const [maxRejectionDraft, setMaxRejectionDraft] = useState(
    String(DEFAULT_MAX_REJECTION_COUNT),
  );
  const [maxRejectionSaving, setMaxRejectionSaving] = useState(false);
  const [maxRejectionError, setMaxRejectionError] = useState('');

  useEffect(() => {
    if (!companyId) {
      setAutoJudgeReview(true);
      setAutoJudgeLoading(false);
      setMaxRejectionDraft(String(DEFAULT_MAX_REJECTION_COUNT));
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setAutoJudgeLoading(true);
      setAutoJudgeError('');
      setMaxRejectionError('');
      try {
        const snap = await getDoc(doc(db, 'companies', companyId));
        if (cancelled) return;
        const data = snap.exists() ? snap.data() : {};
        setAutoJudgeReview(isAutoJudgeReviewEnabled(data));
        const settings =
          data?.settings && typeof data.settings === 'object'
            ? data.settings
            : {};
        const clamped = clampMaxRejectionCount(settings.maxRejectionCount);
        setMaxRejectionDraft(
          String(clamped ?? DEFAULT_MAX_REJECTION_COUNT),
        );
      } catch (err) {
        console.error('AiWorkforceRosterPanel autoJudgeReview load:', err);
        if (!cancelled) {
          setAutoJudgeError(
            err?.message || 'Could not load Automated QA setting.',
          );
          setAutoJudgeReview(true);
          setMaxRejectionDraft(String(DEFAULT_MAX_REJECTION_COUNT));
        }
      } finally {
        if (!cancelled) setAutoJudgeLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  /** Deep-link from Friction insights: prefill AgentDialog only (no auto-save). */
  useEffect(() => {
    const sid =
      typeof frictionSuggestionId === 'string'
        ? frictionSuggestionId.trim()
        : '';
    if (!sid || !companyId || agentsLoading) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'frictionSuggestions', sid));
        if (cancelled) return;
        if (!snap.exists()) {
          setSeedError(`Friction suggestion ${sid} not found.`);
          onFrictionDeepLinkConsumed?.();
          return;
        }
        const data = snap.data() || {};
        if (data.companyId && data.companyId !== companyId) {
          setSeedError('Friction suggestion belongs to another company.');
          onFrictionDeepLinkConsumed?.();
          return;
        }
        const status = typeof data.status === 'string' ? data.status : '';
        if (status !== 'approved_manual') {
          setSeedError(
            'Approve for manual apply in Reports first, then open the roster editor.',
          );
          onFrictionDeepLinkConsumed?.();
          return;
        }
        const roleHint =
          (typeof agentRoleHint === 'string' && agentRoleHint.trim()) ||
          (typeof data.slice?.assigneeAgentRole === 'string' &&
            data.slice.assigneeAgentRole.trim()) ||
          '';
        const roster = (studioAgents || []).map((a) =>
          normalizeStudioAgent(a.id, a),
        );
        const match =
          roster.find(
            (a) =>
              roleHint &&
              String(a.roleTitle || '')
                .trim()
                .toLowerCase() === roleHint.toLowerCase(),
          ) ||
          roster.find(
            (a) => !isCodeReviewerAgent(a) && !isCustomerSuccessAgent(a),
          );
        if (!match) {
          setSeedError(
            roleHint
              ? `No roster agent matched role “${roleHint}”. Open an agent manually and paste the draft.`
              : 'No roster agent available to prefill.',
          );
          onFrictionDeepLinkConsumed?.();
          return;
        }
        setFrictionDraftText(
          typeof data.draftText === 'string' && data.draftText.trim()
            ? data.draftText
            : typeof data.proposedAction === 'string'
              ? data.proposedAction
              : '',
        );
        setActiveFrictionSuggestionId(sid);
        setEditingAgent(toAgentDialogAgent(match, studioAgents));
        onFrictionDeepLinkConsumed?.();
      } catch (err) {
        console.error('AiWorkforceRosterPanel friction deep-link:', err);
        if (!cancelled) {
          setSeedError(
            err?.message || 'Could not open friction suggestion prefill.',
          );
          onFrictionDeepLinkConsumed?.();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Consume once per suggestion id; avoid re-open loops on roster refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [frictionSuggestionId, companyId, agentsLoading]);

  const handleAutoJudgeToggle = async (checked) => {
    if (!companyId || autoJudgeSaving) return;
    setAutoJudgeSaving(true);
    setAutoJudgeError('');
    const previous = autoJudgeReview;
    setAutoJudgeReview(checked);
    try {
      await updateDoc(doc(db, 'companies', companyId), {
        'settings.autoJudgeReview': checked,
        updatedAt: serverTimestamp(),
        updatedByUid: auth.currentUser?.uid || null,
      });
    } catch (err) {
      console.error('AiWorkforceRosterPanel autoJudgeReview save:', err);
      setAutoJudgeReview(previous);
      setAutoJudgeError(
        err?.message || 'Could not save Automated QA setting.',
      );
    } finally {
      setAutoJudgeSaving(false);
    }
  };

  const handleMaxRejectionBlur = async () => {
    if (!companyId || maxRejectionSaving || autoJudgeLoading) return;
    const clamped = clampMaxRejectionCount(maxRejectionDraft);
    if (clamped == null) {
      setMaxRejectionError(
        `Enter an integer from ${MIN_MAX_REJECTION_COUNT} to ${MAX_MAX_REJECTION_COUNT}.`,
      );
      return;
    }
    setMaxRejectionError('');
    setMaxRejectionDraft(String(clamped));
    setMaxRejectionSaving(true);
    try {
      await updateDoc(doc(db, 'companies', companyId), {
        'settings.maxRejectionCount': clamped,
        updatedAt: serverTimestamp(),
        updatedByUid: auth.currentUser?.uid || null,
      });
    } catch (err) {
      console.error('AiWorkforceRosterPanel maxRejectionCount save:', err);
      setMaxRejectionError(
        err?.message || 'Could not save max rejection count.',
      );
    } finally {
      setMaxRejectionSaving(false);
    }
  };

  const rosterAgents = useMemo(() => {
    const rows = (studioAgents || []).map((agent) => {
      const normalized = normalizeStudioAgent(agent.id, agent);
      return {
        ...normalized,
        isCodeReviewer: normalized.isCodeReviewer || isCodeReviewerAgent(agent),
        isCustomerSuccess:
          normalized.isCustomerSuccess || isCustomerSuccessAgent(agent),
      };
    });
    rows.sort((a, b) => {
      if (a.isCodeReviewer && !b.isCodeReviewer) return -1;
      if (!a.isCodeReviewer && b.isCodeReviewer) return 1;
      if (a.isCustomerSuccess && !b.isCustomerSuccess) return -1;
      if (!a.isCustomerSuccess && b.isCustomerSuccess) return 1;
      return a.agentName.localeCompare(b.agentName);
    });
    return rows;
  }, [studioAgents]);

  // Ensure Code Reviewer exists in the shared `ai_agents` collection.
  useEffect(() => {
    if (!companyId || agentsLoading) return undefined;

    const hasReviewer = (studioAgents || []).some(isCodeReviewerAgent);
    if (hasReviewer) return undefined;

    let cancelled = false;

    const seedCodeReviewer = async () => {
      setSeedingReviewer(true);
      setSeedError('');
      try {
        const actor = auth.currentUser;
        const reviewerId = codeReviewerDocId(companyId);
        const reviewerRef = doc(db, 'ai_agents', reviewerId);

        // Prefer migrating any legacy companies/{id}/ai_roster/code_reviewer doc.
        let overrides = {};
        try {
          const legacySnap = await getDoc(
            doc(db, 'companies', companyId, 'ai_roster', CODE_REVIEWER_AGENT_ID),
          );
          if (legacySnap.exists()) {
            overrides = legacySnap.data() || {};
          }
        } catch (legacyErr) {
          console.warn('ai_roster migration read skipped:', legacyErr);
        }

        if (cancelled) return;

        const existing = await getDoc(reviewerRef);
        if (existing.exists()) {
          await setDoc(
            reviewerRef,
            {
              companyId,
              rosterRole: CODE_REVIEWER_AGENT_ID,
              isCodeReviewer: true,
              modelTarget: normalizeModelTarget(
                existing.data()?.modelTarget ||
                  overrides.modelTarget ||
                  DEFAULT_MODEL_TARGET,
              ),
              updatedAt: serverTimestamp(),
              updatedBy: actor?.uid || null,
            },
            { merge: true },
          );
          return;
        }

        const payload = buildCodeReviewerSeedPayload(
          companyId,
          actor?.uid || null,
          actor?.email || '',
          overrides,
        );
        payload.createdAt = serverTimestamp();
        payload.updatedAt = serverTimestamp();
        await setDoc(reviewerRef, payload);
      } catch (e) {
        console.error('seed code reviewer into ai_agents:', e);
        if (!cancelled) {
          setSeedError(e.message || 'Could not initialize the Code Reviewer agent.');
        }
      } finally {
        if (!cancelled) setSeedingReviewer(false);
      }
    };

    seedCodeReviewer();
    return () => {
      cancelled = true;
    };
  }, [companyId, agentsLoading, studioAgents]);

  // Ensure Customer Success Agent exists for SHIFTYPM_HQ only.
  useEffect(() => {
    if (!companyId || agentsLoading) return undefined;
    if (companyId !== SHIFTYPM_HQ_COMPANY_ID) return undefined;

    const hasCsa = (studioAgents || []).some(isCustomerSuccessAgent);
    if (hasCsa) return undefined;

    let cancelled = false;

    const seedCustomerSuccess = async () => {
      setSeedingCsa(true);
      setSeedError('');
      try {
        const actor = auth.currentUser;
        const csaId = customerSuccessDocId(companyId);
        const csaRef = doc(db, 'ai_agents', csaId);

        let overrides = {};
        try {
          const legacySnap = await getDoc(
            doc(db, 'companies', companyId, 'ai_roster', CUSTOMER_SUCCESS_AGENT_ID),
          );
          if (legacySnap.exists()) {
            overrides = legacySnap.data() || {};
          }
        } catch (legacyErr) {
          console.warn('ai_roster CSA migration read skipped:', legacyErr);
        }

        if (cancelled) return;

        const existing = await getDoc(csaRef);
        if (existing.exists()) {
          await setDoc(
            csaRef,
            {
              companyId,
              rosterRole: CUSTOMER_SUCCESS_AGENT_ID,
              isCustomerSuccess: true,
              department: 'Support',
              allowedTools: ['update_ticket_status', 'reply_to_ticket'],
              modelTarget: normalizeModelTarget(
                existing.data()?.modelTarget ||
                  overrides.modelTarget ||
                  CSA_MODEL_TARGET,
              ),
              updatedAt: serverTimestamp(),
              updatedBy: actor?.uid || null,
            },
            { merge: true },
          );
          return;
        }

        const payload = buildCustomerSuccessSeedPayload(
          companyId,
          actor?.uid || null,
          actor?.email || '',
          overrides,
        );
        payload.createdAt = serverTimestamp();
        payload.updatedAt = serverTimestamp();
        await setDoc(csaRef, payload);
      } catch (e) {
        console.error('seed customer success agent into ai_agents:', e);
        if (!cancelled) {
          setSeedError(
            e.message || 'Could not initialize the Customer Success Agent.',
          );
        }
      } finally {
        if (!cancelled) setSeedingCsa(false);
      }
    };

    seedCustomerSuccess();
    return () => {
      cancelled = true;
    };
  }, [companyId, agentsLoading, studioAgents]);

  if (!companyId) {
    return (
      <Alert severity="info">
        Link a workspace company to configure the AI workforce roster.
      </Alert>
    );
  }

  const loading = agentsLoading || seedingReviewer || seedingCsa;
  const loadError =
    (typeof agentsError === 'string' ? agentsError : agentsError?.message) ||
    seedError ||
    '';

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'flex-start' }}
        spacing={2}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
            AI Workforce Roster
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Hire and manage autonomous agents for this workspace. Set model targets,
            status, capabilities, and system prompts in one place.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddAlt1Icon />}
          onClick={() => setHireOpen(true)}
          sx={{ flexShrink: 0, fontWeight: 700 }}
        >
          Hire agent
        </Button>
      </Stack>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoJudgeReview}
                  onChange={(_, checked) => handleAutoJudgeToggle(checked)}
                  disabled={autoJudgeLoading || autoJudgeSaving || !companyId}
                />
              }
              label="Auto-run Automated QA on new patches"
            />
            <Typography variant="body2" color="text.secondary">
              When off, new patches land as &quot;Awaiting Automated QA request&quot;.
              A human must click <strong>Run Automated QA</strong> in Code
              Review (not Code Court) before the merge-gating Judge runs.
            </Typography>
            <TextField
              size="small"
              label="Max Automated QA rejections before human intervention"
              value={maxRejectionDraft}
              onChange={(e) => setMaxRejectionDraft(e.target.value)}
              onBlur={() => void handleMaxRejectionBlur()}
              disabled={
                autoJudgeLoading || maxRejectionSaving || !companyId
              }
              type="number"
              inputProps={{
                min: MIN_MAX_REJECTION_COUNT,
                max: MAX_MAX_REJECTION_COUNT,
                step: 1,
              }}
              helperText={`Default ${DEFAULT_MAX_REJECTION_COUNT}. Allowed range ${MIN_MAX_REJECTION_COUNT}–${MAX_MAX_REJECTION_COUNT}. Saves when you leave the field.`}
              sx={{ maxWidth: 420, mt: 1 }}
            />
            {maxRejectionError ? (
              <Alert severity="error" onClose={() => setMaxRejectionError('')}>
                {maxRejectionError}
              </Alert>
            ) : null}
            {autoJudgeError ? (
              <Alert
                severity="error"
                onClose={() => setAutoJudgeError('')}
              >
                {autoJudgeError}
              </Alert>
            ) : null}
          </Stack>
        </CardContent>
      </Card>

      {loadError && (
        <Alert severity="error" onClose={() => setSeedError('')}>
          {loadError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : rosterAgents.length === 0 ? (
        <Alert severity="info">
          No AI agents yet. Use Hire agent above, or wait for the Code Reviewer
          to initialize automatically.
        </Alert>
      ) : (
        <Card variant="outlined">
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ display: 'block', px: 2, pt: 2, pb: 1 }}
            >
              ROSTER ({rosterAgents.length})
            </Typography>
            <List dense disablePadding>
              {rosterAgents.map((agent) => {
                const Icon = agent.isCodeReviewer
                  ? GavelIcon
                  : agent.isCustomerSuccess
                    ? SupportAgentIcon
                    : SmartToyOutlinedIcon;
                return (
                  <ListItem
                    key={agent.id}
                    sx={{ px: 2, py: 1.25 }}
                    secondaryAction={(
                      <Stack direction="row" spacing={0.25}>
                        <Tooltip title="Edit agent">
                          <IconButton
                            edge="end"
                            size="small"
                            color="primary"
                            aria-label="Edit agent"
                            onClick={() =>
                              setEditingAgent(
                                toAgentDialogAgent(agent, studioAgents),
                              )
                            }
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove agent">
                          <IconButton
                            edge="end"
                            size="small"
                            color="error"
                            aria-label="Remove agent"
                            onClick={() =>
                              setRemovingAgent(
                                toAgentDialogAgent(agent, studioAgents),
                              )
                            }
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Icon
                        fontSize="small"
                        color={agent.status === 'enabled' ? 'primary' : 'disabled'}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={agent.agentName}
                      secondary={
                        agent.status === 'enabled' ?
                          `${agent.roleTitle || 'Agent'} · ${agent.modelTarget}` :
                          'Disabled'
                      }
                      primaryTypographyProps={{ fontWeight: 600, noWrap: true }}
                      secondaryTypographyProps={{ noWrap: true }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </CardContent>
        </Card>
      )}

      <AgentDialog
        open={hireOpen}
        onClose={() => setHireOpen(false)}
        companyId={companyId}
      />

      <AgentDialog
        open={Boolean(editingAgent)}
        onClose={() => {
          setEditingAgent(null);
          setFrictionDraftText(null);
          setActiveFrictionSuggestionId(null);
        }}
        companyId={companyId}
        agent={editingAgent}
        frictionSuggestionId={activeFrictionSuggestionId}
        frictionDraftText={frictionDraftText}
      />

      <RemoveAgentDialog
        open={Boolean(removingAgent)}
        agent={removingAgent}
        onClose={() => setRemovingAgent(null)}
      />
    </Stack>
  );
}
